import { ExperimentResults } from "./experiment.entity";

/**
 * A `required_visitors` above this is not a plan, it is the backend's int64 "unset" sentinel
 * leaking through (the results screen was shipping `9.223.372.036.854.776.000 visitantes` at one
 * point). Anything larger is treated exactly like `0`: there is no target to measure against.
 */
const MAX_PLAUSIBLE_REQUIRED_VISITORS = 1e9;

/** `requiredVisitors` is only a usable denominator when it is a real, plausible, positive count. */
export function hasSampleTarget(requiredVisitors: number): boolean {
  return (
    Number.isFinite(requiredVisitors) &&
    requiredVisitors > 0 &&
    requiredVisitors <= MAX_PLAUSIBLE_REQUIRED_VISITORS
  );
}

/**
 * The three conditions `backoffice-manager` applies before it will declare any winner, expressed
 * as far as this response lets the client tell. Purely explanatory: the verdict itself is already
 * decided server-side — this only turns "still collecting" into "still collecting *because*", and
 * is the single derivation shared by the decision panel and the comparison row's pending pill, so
 * the two can never disagree.
 */
export interface DecisionGates {
  /** Collected valid visitors, counted — never back-derived from `sampleProgress × required`. */
  collected: number;
  /** `false` when the backend could not size the test (no target, or the int64 sentinel). */
  hasTarget: boolean;
  sampleMet: boolean;
  bookingsMet: boolean;
  /** `minDays === 0` means "unset" on the wire, which no elapsed day count can fail. */
  durationMet: boolean;
  /** True only when all three are met — the client's own reading, never a substitute for
   *  `isReliable`, which stays the backend's answer. */
  allMet: boolean;
}

export function resolveDecisionGates(data: ExperimentResults): DecisionGates {
  const collected = data.scenarioA.validVisitors + data.scenarioB.validVisitors;
  const hasTarget = hasSampleTarget(data.requiredVisitors);
  // With no target there is nothing to fall short of, so the gate cannot be reported as unmet.
  const sampleMet = !hasTarget || data.sampleProgress >= 1;
  const bookingsMet = data.minBookingsReached;
  const durationMet = data.minDays <= 0 || data.activeDays >= data.minDays;

  return {
    collected,
    hasTarget,
    sampleMet,
    bookingsMet,
    durationMet,
    allMet: sampleMet && bookingsMet && durationMet,
  };
}
